import { useState, useRef, useEffect, useCallback } from 'react';
import { sendEmailWithAttachments, queueSend, saveDraft, updateDraft, deleteDraft, getSignatures } from '../../services/emailApi';
import ChipInput from './ChipInput';
import RichTextEditor from './RichTextEditor';

const SIG_SEPARATOR = '\n\n--\n';
const SIG_SEPARATOR_HTML = '<div class="signature" style="margin-top:16px;border-top:1px solid #e5e7eb;padding-top:12px;color:#6b7280">';

/**
 * Compose modal supporting: new message, reply, reply all, forward.
 * Features: CC/BCC, chip inputs with auto-complete, rich text, signatures, draft auto-save, undo send.
 */
export default function ComposeModal({ mode = 'compose', from, replyTo, onClose, onSent, onUndoSend }) {
  // Initialize recipients based on mode
  const initTo = () => {
    if (mode === 'reply' || mode === 'replyAll') return [replyTo?.from_address].filter(Boolean);
    return [];
  };
  const initCc = () => {
    if (mode === 'replyAll' && replyTo) {
      const all = [
        ...(replyTo.to_address || '').split(',').map((s) => s.trim()),
        ...(replyTo.cc_addresses || '').split(',').map((s) => s.trim()),
      ].filter((e) => e && e !== from && e !== replyTo.from_address);
      return [...new Set(all)];
    }
    return [];
  };
  const initSubject = () => {
    if (!replyTo) return '';
    const subj = replyTo.subject || '';
    if (mode === 'forward') return `Fwd: ${subj.replace(/^Fwd:\s*/i, '')}`;
    if (mode === 'reply' || mode === 'replyAll') return `Re: ${subj.replace(/^Re:\s*/i, '')}`;
    return '';
  };
  const initBodyHtml = () => {
    if (!replyTo) return '';
    const text = replyTo.body_text || '';
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
    if (mode === 'forward') {
      return `<br><br><blockquote style="border-left:2px solid #d1d5db;padding-left:12px;color:#6b7280;margin:0"><p><strong>Forwarded message</strong><br>From: ${replyTo.from_address || ''}<br>Date: ${replyTo.created_at ? new Date(replyTo.created_at).toLocaleString() : ''}<br>Subject: ${replyTo.subject || ''}<br>To: ${replyTo.to_address || ''}</p>${escaped}</blockquote>`;
    }
    return `<br><br><blockquote style="border-left:2px solid #d1d5db;padding-left:12px;color:#6b7280;margin:0">${escaped}</blockquote>`;
  };

  const [toAddresses, setToAddresses] = useState(initTo);
  const [ccAddresses, setCcAddresses] = useState(initCc);
  const [bccAddresses, setBccAddresses] = useState([]);
  const [showCc, setShowCc] = useState(() => mode === 'replyAll' && initCc().length > 0);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(initSubject);
  const [bodyHtml, setBodyHtml] = useState(initBodyHtml);
  const [bodyText, setBodyText] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [minimized, setMinimized] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [selectedSigId, setSelectedSigId] = useState(null);
  const fileInputRef = useRef(null);
  const lastSavedRef = useRef('');

  // Load signatures and auto-append default
  useEffect(() => {
    getSignatures().then((data) => {
      const sigs = data.signatures || [];
      setSignatures(sigs);
      if (mode === 'compose' && sigs.length > 0) {
        const defaultSig = sigs.find((s) => s.is_default) || sigs[0];
        if (defaultSig) {
          setSelectedSigId(defaultSig.id);
          setBodyHtml((prev) => {
            if (prev) return prev;
            return `<p></p>${SIG_SEPARATOR_HTML}${defaultSig.body_html || defaultSig.body_text.replace(/\n/g, '<br>')}</div>`;
          });
        }
      }
    }).catch(() => {});
  }, []);

  // Switch signature
  function handleSigChange(sigId) {
    setSelectedSigId(sigId);
    const sig = signatures.find((s) => s.id === sigId);
    if (!sig) return;

    setBodyHtml((prev) => {
      // Remove old signature block and append new one
      const cleaned = prev.replace(/<div class="signature"[^>]*>[\s\S]*?<\/div>$/i, '').trimEnd();
      return `${cleaned}${SIG_SEPARATOR_HTML}${sig.body_html || sig.body_text.replace(/\n/g, '<br>')}</div>`;
    });
  }

  // Ctrl+Enter to send
  useEffect(() => {
    function handler(e) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (toAddresses.length > 0 && !sending) handleSend();
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toAddresses, sending, subject, bodyHtml, bodyText, ccAddresses, bccAddresses, files]);

  // Draft auto-save every 30s
  const getContentHash = useCallback(() => {
    return JSON.stringify({ to: toAddresses, cc: ccAddresses, bcc: bccAddresses, subject, body: bodyText });
  }, [toAddresses, ccAddresses, bccAddresses, subject, bodyText]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const hash = getContentHash();
      if (hash === lastSavedRef.current) return;
      if (!subject?.trim() && !bodyText?.trim() && toAddresses.length === 0) return;
      try {
        const data = {
          to: toAddresses.join(', '),
          subject,
          text: bodyText,
          html: bodyHtml,
          cc: ccAddresses.join(', '),
          bcc: bccAddresses.join(', '),
        };
        if (draftId) {
          await updateDraft(draftId, data);
        } else {
          const res = await saveDraft(data);
          const newId = res?.data?.draftId || res?.draftId || res?.draft?.id;
          if (newId) setDraftId(newId);
        }
        lastSavedRef.current = hash;
        setDraftSaved(true);
        setTimeout(() => setDraftSaved(false), 3000);
      } catch { /* silent */ }
    }, 5000); // Auto-save every 5 seconds
    return () => clearInterval(interval);
  }, [getContentHash, draftId]);

  function handleFileSelect(e) {
    setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    e.target.value = '';
  }

  function removeFile(index) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSend() {
    if (toAddresses.length === 0) return;
    setSending(true);
    setError('');
    try {
      const to = toAddresses.join(', ');
      const cc = ccAddresses.length > 0 ? ccAddresses.join(', ') : undefined;
      const bcc = bccAddresses.length > 0 ? bccAddresses.join(', ') : undefined;

      if (files.length === 0 && onUndoSend) {
        const res = await queueSend({
          to, cc, bcc, subject, text: bodyText, html: bodyHtml,
          inReplyTo: replyTo?.provider_message_id || undefined,
          threadId: replyTo?.thread_id || undefined,
        });
        if (draftId) { try { await deleteDraft(draftId); } catch {} }
        onUndoSend(res.email);
        onSent();
        return;
      }

      await sendEmailWithAttachments({
        to, cc, bcc, subject, text: bodyText, html: bodyHtml,
        inReplyTo: replyTo?.provider_message_id || undefined,
        threadId: replyTo?.thread_id || undefined,
        files,
      });
      if (draftId) { try { await deleteDraft(draftId); } catch {} }
      onSent();
    } catch (err) {
      setError(err.response?.data?.error || 'Send failed');
    } finally {
      setSending(false);
    }
  }

  async function handleClose() {
    // Save draft on close if there's any content
    const hasContent = subject?.trim() || bodyText?.trim() || toAddresses.length > 0;
    if (hasContent) {
      try {
        const data = {
          to: toAddresses.join(', '),
          subject,
          text: bodyText,
          html: bodyHtml,
          cc: ccAddresses.join(', '),
          bcc: bccAddresses.join(', '),
        };
        if (draftId) {
          await updateDraft(draftId, data);
        } else {
          await saveDraft(data);
        }
      } catch { /* silent */ }
    }
    onClose();
  }

  function handleDiscard() {
    if (draftId) { deleteDraft(draftId).catch(() => {}); }
    onClose();
  }

  const modeLabel = { compose: 'New message', reply: 'Reply', replyAll: 'Reply all', forward: 'Forward' }[mode] || 'New message';

  if (minimized) {
    return (
      <div onClick={() => setMinimized(false)}
        className="fixed bottom-0 right-20 z-50 flex h-10 w-72 cursor-pointer items-center justify-between rounded-t-lg bg-gray-900 px-4 text-sm text-white shadow-lg">
        <span className="truncate">{subject || modeLabel}</span>
        <button onClick={(e) => { e.stopPropagation(); handleClose(); }} className="text-gray-400 hover:text-white">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 right-20 z-50 flex w-[560px] max-w-[calc(100vw-2rem)] flex-col rounded-t-xl border border-gray-300 bg-white shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between rounded-t-xl bg-gray-900 px-4 py-2.5">
        <span className="text-sm font-medium text-white">{modeLabel}</span>
        <div className="flex gap-1">
          <button onClick={() => setMinimized(true)} className="rounded p-1 text-gray-400 hover:text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" /></svg>
          </button>
          <button onClick={handleClose} className="rounded p-1 text-gray-400 hover:text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="flex flex-col">
        <div className="border-b border-gray-200 px-4 py-1.5">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">From</span>
            <span className="text-gray-700">{from}</span>
          </div>
        </div>
        <div className="flex items-center border-b border-gray-200 px-4 py-1.5">
          <span className="mr-2 text-sm text-gray-500">To</span>
          <div className="flex-1"><ChipInput value={toAddresses} onChange={setToAddresses} placeholder="Recipients" autoFocus /></div>
          {!showCc && !showBcc && (
            <div className="flex gap-1 text-xs">
              <button tabIndex={-1} onClick={() => setShowCc(true)} className="text-gray-400 hover:text-indigo-600">Cc</button>
              <button tabIndex={-1} onClick={() => setShowBcc(true)} className="text-gray-400 hover:text-indigo-600">Bcc</button>
            </div>
          )}
        </div>
        {showCc && (
          <div className="flex items-center border-b border-gray-200 px-4 py-1.5">
            <span className="mr-2 text-sm text-gray-500">Cc</span>
            <div className="flex-1"><ChipInput value={ccAddresses} onChange={setCcAddresses} placeholder="" /></div>
            {!showBcc && <button tabIndex={-1} onClick={() => setShowBcc(true)} className="text-xs text-gray-400 hover:text-indigo-600">Bcc</button>}
          </div>
        )}
        {showBcc && (
          <div className="flex items-center border-b border-gray-200 px-4 py-1.5">
            <span className="mr-2 text-sm text-gray-500">Bcc</span>
            <div className="flex-1"><ChipInput value={bccAddresses} onChange={setBccAddresses} placeholder="" /></div>
          </div>
        )}
        <div className="border-b border-gray-200 px-4 py-1.5">
          <input type="text" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)}
            className="w-full border-0 py-0.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none" />
        </div>

        {/* Rich text editor */}
        <div className="max-h-[40vh] overflow-y-auto">
          <RichTextEditor
            content={bodyHtml}
            onChange={(html, text) => { setBodyHtml(html); setBodyText(text); }}
            placeholder="Write your message..."
          />
        </div>
      </div>

      {/* Attachments */}
      {files.length > 0 && (
        <div className="border-t border-gray-100 px-4 py-2">
          <div className="flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-1.5 rounded bg-gray-100 px-2 py-1 text-xs text-gray-700">
                <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                <span className="truncate max-w-[120px]">{f.name}</span>
                <span className="text-gray-400">{f.size > 1048576 ? `${(f.size / 1048576).toFixed(1)}MB` : `${Math.round(f.size / 1024)}KB`}</span>
                <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 ml-0.5">&times;</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      {error && <div className="px-4 pb-2 text-xs text-red-500">{error}</div>}
      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <button onClick={handleSend} disabled={sending || toAddresses.length === 0}
            className="rounded-full bg-indigo-600 px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-50">
            {sending ? 'Sending...' : 'Send'}
          </button>
          {draftSaved && <span className="text-xs text-gray-400">Draft saved</span>}
          <span className="text-[10px] text-gray-300">Ctrl+Enter</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Signature selector */}
          {signatures.length > 0 && (
            <select
              value={selectedSigId || ''}
              onChange={(e) => handleSigChange(e.target.value)}
              className="rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-500 focus:outline-none"
              title="Signature"
            >
              <option value="">No signature</option>
              {signatures.map((sig) => (
                <option key={sig.id} value={sig.id}>{sig.name}</option>
              ))}
            </select>
          )}
          <button onClick={() => fileInputRef.current?.click()} className="rounded p-1.5 text-gray-400 hover:text-gray-600" title="Attach files">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
          </button>
          <button onClick={handleDiscard} className="rounded p-1.5 text-gray-400 hover:text-red-500" title="Discard">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
}
